import React, { Fragment, useState, useEffect, useMemo } from "react";
import Breadcrumb from "../../common/breadcrumb/breadcrumb";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import { API_GRADE, API_USER, API_SYSTEMS } from "../../../redux/apiRoutes";
import { useSelector } from "react-redux";
import axios from "axios";
import SweetAlert from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { Typeahead } from "react-bootstrap-typeahead";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Input,
  Badge,
} from "reactstrap";
import classnames from "classnames";
import { Trash2, Edit3, Shield, FileText, Send, UploadCloud, Users, CheckCircle, AlertTriangle } from "react-feather";

function Systems() {
  const user = useSelector((content) => content.User);
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("1");
  
  // Data lists
  const [students, setStudents] = useState([]);
  const [parents, setParents] = useState([]);
  const [grades, setGrades] = useState([]);
  
  // Loaders
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [loadingParents, setLoadingParents] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Selections
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedParents, setSelectedParents] = useState([]);

  // Modals
  const [gradeModal, setGradeModal] = useState(false);
  const [gradeSelected, setGradeSelected] = useState([]);

  // Search filters
  const [studentSearch, setStudentSearch] = useState("");
  const [parentSearch, setParentSearch] = useState("");
  const [selectedGradeFilter, setSelectedGradeFilter] = useState("");

  const toggleGradeModal = () => {
    setGradeModal(!gradeModal);
    setGradeSelected([]);
  };

  // Fetch list of students
  const fetchStudents = async () => {
    setLoadingStudents(true);
    try {
      const response = await axios.get(`${API_USER}?status=ACTIVE,DISABLED&role=STUDENT`);
      if (response.data && response.data.docs) {
        setStudents(response.data.docs);
      }
    } catch (e) {
      toast.error("Error al obtener listado de alumnos: " + e.message);
    } finally {
      setLoadingStudents(false);
    }
  };

  // Fetch list of parents
  const fetchParents = async () => {
    setLoadingParents(true);
    try {
      const response = await axios.get(`${API_USER}?status=ACTIVE,DISABLED&role=TUTOR`);
      if (response.data && response.data.docs) {
        setParents(response.data.docs);
      }
    } catch (e) {
      toast.error("Error al obtener listado de padres: " + e.message);
    } finally {
      setLoadingParents(false);
    }
  };

  // Fetch list of grades for bulk change salon
  const fetchGrades = async () => {
    try {
      const response = await axios.get(`${API_GRADE}?status=ACTIVE`);
      if (response.data && response.data.docs) {
        const formatted = response.data.docs.map(g => ({
          uuid: g.uuid,
          name: `${g.name} - ${g.level}`
        }));
        setGrades(formatted);
      }
    } catch (e) {
      toast.error("Error al obtener los grados escolares: " + e.message);
    }
  };

  useEffect(() => {
    if (user.role !== "SISTEMAS") {
      navigate(`${process.env.PUBLIC_URL}/inicio`);
      return;
    }
    fetchStudents();
    fetchParents();
    fetchGrades();
  }, [user, navigate]);

  // Filters
  const filteredStudents = useMemo(() => {
    return students.filter(student => {
      const matchesSearch = 
        !studentSearch ||
        (student.names && student.names.toLowerCase().includes(studentSearch.toLowerCase())) ||
        (student.second_names && student.second_names.toLowerCase().includes(studentSearch.toLowerCase())) ||
        (student.nia && student.nia.toLowerCase().includes(studentSearch.toLowerCase()));

      const matchesGrade = 
        !selectedGradeFilter ||
        (selectedGradeFilter === "none" && (!student.grade || student.grade.length === 0)) ||
        (student.grade && student.grade.some(g => g.uuid === selectedGradeFilter));

      return matchesSearch && matchesGrade;
    });
  }, [students, studentSearch, selectedGradeFilter]);

  const filteredParents = useMemo(() => {
    return parents.filter(parent => 
      (parent.names && parent.names.toLowerCase().includes(parentSearch.toLowerCase())) ||
      (parent.second_names && parent.second_names.toLowerCase().includes(parentSearch.toLowerCase())) ||
      (parent.email && parent.email.toLowerCase().includes(parentSearch.toLowerCase()))
    );
  }, [parents, parentSearch]);

  // Bulk Actions Handlers
  const handleBulkDeleteStudents = () => {
    const ids = selectedStudents.map(s => s.uuid);
    SweetAlert.fire({
      title: "¿Eliminar Alumnos Seleccionados?",
      text: `Esta acción eliminará permanentemente a ${ids.length} alumnos de la plataforma y los removerá de los perfiles de sus padres.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
      reverseButtons: true,
    }).then(async (result) => {
      if (result.value) {
        setActionLoading(true);
        try {
          await axios.post(`${API_SYSTEMS}/students/bulk-delete`, { studentUuids: ids });
          SweetAlert.fire("Eliminados", "Los alumnos han sido eliminados de la plataforma.", "success");
          setSelectedStudents([]);
          fetchStudents();
          fetchParents();
        } catch (e) {
          toast.error("Error en la eliminación masiva: " + e.message);
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  const handleBulkChangeGrade = async () => {
    if (gradeSelected.length === 0) {
      toast.error("Por favor, selecciona un grado escolar de la lista.");
      return;
    }
    const ids = selectedStudents.map(s => s.uuid);
    const targetGrade = gradeSelected[0];

    setActionLoading(true);
    try {
      await axios.post(`${API_SYSTEMS}/students/bulk-change-salon`, {
        studentUuids: ids,
        gradeUuid: targetGrade.uuid
      });
      SweetAlert.fire("Actualizado", `Se cambió el salón de ${ids.length} alumnos a ${targetGrade.name}.`, "success");
      toggleGradeModal();
      setSelectedStudents([]);
      fetchStudents();
      fetchParents();
    } catch (e) {
      toast.error("Error al actualizar grado: " + e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkDeleteParents = () => {
    const ids = selectedParents.map(p => p.uuid);
    SweetAlert.fire({
      title: "¿Eliminar Padres y sus Alumnos?",
      text: `Esta acción eliminará permanentemente a ${ids.length} padres y a TODOS sus alumnos vinculados en la plataforma.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar todos",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
      reverseButtons: true,
    }).then(async (result) => {
      if (result.value) {
        setActionLoading(true);
        try {
          await axios.post(`${API_SYSTEMS}/parents/bulk-delete`, { parentUuids: ids });
          SweetAlert.fire("Eliminados", "Los padres y sus alumnos vinculados han sido eliminados.", "success");
          setSelectedParents([]);
          fetchParents();
          fetchStudents();
        } catch (e) {
          toast.error("Error al eliminar padres masivamente: " + e.message);
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  // CSV Imports Handler
  const handleCSVUpload = async (type, file) => {
    if (!file) return;
    if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
      toast.error("Solo se aceptan archivos en formato CSV (.csv)");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setActionLoading(true);
    try {
      const endpoint = type === "students" ? "bulk-upload/students" : "bulk-upload/parents";
      const response = await axios.post(`${API_SYSTEMS}/${endpoint}`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      const { createdCount, errors } = response.data;
      if (errors && errors.length > 0) {
        let errorMsg = errors.map(err => `Fila ${err.row}: ${err.error}`).join("<br/>");
        SweetAlert.fire({
          title: `Carga completada con advertencias`,
          html: `<p>Se crearon <b>${createdCount}</b> registros con éxito.</p>
                 <div style="text-align: left; max-height: 200px; overflow-y: auto; background: #fee; padding: 10px; border: 1px solid #fcc; border-radius: 5px;">
                   ${errorMsg}
                 </div>`,
          icon: "warning"
        });
      } else {
        SweetAlert.fire("Carga exitosa", `Se han cargado <b>${createdCount}</b> registros sin errores.`, "success");
      }

      if (type === "students") {
        fetchStudents();
      } else {
        fetchParents();
      }
    } catch (e) {
      toast.error("Error al procesar el archivo CSV: " + e.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Send Welcome Email to All Parents
  const handleSendEmailsAllParents = () => {
    SweetAlert.fire({
      title: "¿Enviar credenciales de acceso a todos los padres?",
      text: "Se enviará un correo electrónico de bienvenida con su respectivo usuario y contraseña a todos los padres registrados en la plataforma.",
      icon: "info",
      showCancelButton: true,
      confirmButtonText: "Sí, enviar correos",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
    }).then(async (result) => {
      if (result.value) {
        setActionLoading(true);
        try {
          const response = await axios.post(`${API_SYSTEMS}/welcome-email-all-parents`);
          SweetAlert.fire("Completado", response.data.message || "Correos enviados con éxito.", "success");
        } catch (e) {
          toast.error("Error al enviar correos masivos: " + e.message);
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  // Nuclear Cleanup Handler
  const handleClearAcademicData = () => {
    SweetAlert.fire({
      title: "CUIDADO: ¿Limpiar historial académico?",
      text: "Esta acción es irreversible y eliminará permanentemente TODAS las Tareas, Ausencias, Asistencias y el Monitoreo de Estudiantes. Escribe 'CONFIRMAR' para continuar.",
      input: "text",
      inputPlaceholder: "Escribe CONFIRMAR aquí",
      icon: "error",
      showCancelButton: true,
      confirmButtonText: "Borrar todo",
      confirmButtonColor: "#d33",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
      preConfirm: (inputValue) => {
        if (inputValue !== "CONFIRMAR") {
          SweetAlert.showValidationMessage("Debes escribir exactamente 'CONFIRMAR' para proceder.");
        }
        return inputValue;
      }
    }).then(async (result) => {
      if (result.value === "CONFIRMAR") {
        setActionLoading(true);
        try {
          await axios.delete(`${API_SYSTEMS}/clear-academic-data`);
          SweetAlert.fire("Datos Limpiados", "Se han eliminado todas las tareas, ausencias y asistencias de la base de datos.", "success");
        } catch (e) {
          toast.error("Error al limpiar base de datos: " + e.message);
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  // DataTables Configurations
  const studentColumns = [
    { name: "NIA", selector: row => row.nia, sortable: true, width: "130px" },
    { name: "Nombres", selector: row => row.names, sortable: true },
    { name: "Apellidos", selector: row => row.second_names, sortable: true },
    {
      name: "Grado",
      selector: row => (row.grade && row.grade.length > 0) ? row.grade[0].name : "Sin salón",
      sortable: true
    },
    {
      name: "Estado",
      selector: row => row.status === 'DISABLED' ? 
        <Badge color="warning">DESACTIVADO</Badge> : 
        <Badge color="success">ACTIVO</Badge>,
      sortable: true,
      width: "120px"
    }
  ];

  const parentColumns = [
    { name: "Nombre", selector: row => `${row.names} ${row.second_names}`, sortable: true },
    { name: "Email", selector: row => row.email || "Sin email", sortable: true },
    { name: "Teléfono", selector: row => row.phone_1 || "Sin teléfono" },
    {
      name: "Hijos vinculados",
      selector: row => (row.childs && row.childs.length > 0) ? 
        row.childs.map(c => <Badge color="secondary" key={c.uuid} className="me-1 mb-1">{c.name}</Badge>) : 
        <span className="text-muted">Ninguno</span>
    }
  ];

  return (
    <Fragment>
      <Breadcrumb parent="Administrar" title="Sistemas" />
      <Container fluid={true} className="systems-dashboard">
        <Row>
          <Col md="12">
            <Card className="card-glass">
              <CardBody>
                <Nav tabs className="border-bottom-0 mb-4 justify-content-center">
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "1" })}
                      onClick={() => setActiveTab("1")}
                      style={{ cursor: "pointer", fontWeight: "600" }}
                    >
                      <Users size={16} className="me-2" />
                      Bulk Estudiantes
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "2" })}
                      onClick={() => setActiveTab("2")}
                      style={{ cursor: "pointer", fontWeight: "600" }}
                    >
                      <Users size={16} className="me-2" />
                      Bulk Padres
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "3" })}
                      onClick={() => setActiveTab("3")}
                      style={{ cursor: "pointer", fontWeight: "600" }}
                    >
                      <UploadCloud size={16} className="me-2" />
                      Cargas Masivas & Accesos
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "4" })}
                      onClick={() => setActiveTab("4")}
                      style={{ cursor: "pointer", fontWeight: "600", color: "#d33" }}
                    >
                      <Shield size={16} className="me-2" />
                      Limpieza Nuclear
                    </NavLink>
                  </NavItem>
                </Nav>

                {actionLoading && (
                  <div className="text-center py-4 mb-4" style={{ background: "rgba(255,255,255,0.7)", borderRadius: "10px" }}>
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Procesando acción...</span>
                    </div>
                    <p className="mt-2 text-primary fw-bold">Aplicando cambios en el servidor, por favor espera...</p>
                  </div>
                )}

                <TabContent activeTab={activeTab}>
                  {/* TAB 1: STUDENTS BULK */}
                  <TabPane tabId="1">
                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                      <div>
                        <h5>Administración Bulk de Estudiantes</h5>
                        <p className="text-muted mb-0">Selecciona alumnos para eliminarlos o reubicarlos de salón en bulk.</p>
                      </div>
                      <div className="d-flex align-items-center gap-2 mt-2 mt-sm-0">
                        <Input
                          type="select"
                          value={selectedGradeFilter}
                          onChange={(e) => setSelectedGradeFilter(e.target.value)}
                          style={{ width: "200px" }}
                        >
                          <option value="">Todos los grados</option>
                          <option value="none">Sin salón</option>
                          {grades.map(g => (
                            <option key={g.uuid} value={g.uuid}>{g.name}</option>
                          ))}
                        </Input>
                        <Input
                          type="text"
                          placeholder="Buscar alumno..."
                          value={studentSearch}
                          onChange={(e) => setStudentSearch(e.target.value)}
                          style={{ width: "250px" }}
                        />
                        <Button color="secondary" onClick={fetchStudents}>Refrescar</Button>
                      </div>
                    </div>

                    {selectedStudents.length > 0 && (
                      <div className="alert alert-info d-flex align-items-center justify-content-between p-3 animate-fade-in">
                        <span>Has seleccionado <b>{selectedStudents.length}</b> estudiantes.</span>
                        <div className="d-flex gap-2">
                          <Button color="primary" size="sm" onClick={toggleGradeModal}>
                            <Edit3 size={14} className="me-1" /> Cambiar de Salón
                          </Button>
                          <Button color="danger" size="sm" onClick={handleBulkDeleteStudents}>
                            <Trash2 size={14} className="me-1" /> Eliminar Seleccionados
                          </Button>
                        </div>
                      </div>
                    )}

                    {loadingStudents ? (
                      <div className="loader-box">
                        <div className="loader-1"></div>
                      </div>
                    ) : (
                      <DataTable
                        columns={studentColumns}
                        data={filteredStudents}
                        selectableRows
                        onSelectedRowsChange={(state) => setSelectedStudents(state.selectedRows)}
                        selectableRowSelected={row => selectedStudents.some(s => s.uuid === row.uuid)}
                        pagination
                        striped
                        noDataComponent={<div className="p-4 text-center">No se encontraron alumnos</div>}
                      />
                    )}
                  </TabPane>

                  {/* TAB 2: PARENTS BULK */}
                  <TabPane tabId="2">
                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                      <div>
                        <h5>Administración Bulk de Padres</h5>
                        <p className="text-muted mb-0">Selecciona padres para eliminarlos y dar de baja a sus hijos en cascada.</p>
                      </div>
                      <div className="d-flex align-items-center gap-2 mt-2 mt-sm-0">
                        <Input
                          type="text"
                          placeholder="Buscar padre..."
                          value={parentSearch}
                          onChange={(e) => setParentSearch(e.target.value)}
                          style={{ width: "250px" }}
                        />
                        <Button color="secondary" onClick={fetchParents}>Refrescar</Button>
                      </div>
                    </div>

                    {selectedParents.length > 0 && (
                      <div className="alert alert-danger d-flex align-items-center justify-content-between p-3 animate-fade-in">
                        <span>Has seleccionado <b>{selectedParents.length}</b> padres.</span>
                        <Button color="danger" size="sm" onClick={handleBulkDeleteParents}>
                          <Trash2 size={14} className="me-1" /> Eliminar Padres e Hijos Vinculados
                        </Button>
                      </div>
                    )}

                    {loadingParents ? (
                      <div className="loader-box">
                        <div className="loader-1"></div>
                      </div>
                    ) : (
                      <DataTable
                        columns={parentColumns}
                        data={filteredParents}
                        selectableRows
                        onSelectedRowsChange={(state) => setSelectedParents(state.selectedRows)}
                        selectableRowSelected={row => selectedParents.some(p => p.uuid === row.uuid)}
                        pagination
                        striped
                        noDataComponent={<div className="p-4 text-center">No se encontraron padres</div>}
                      />
                    )}
                  </TabPane>

                  {/* TAB 3: CSV IMPORT & CREDENTIALS DISPATCH */}
                  <TabPane tabId="3">
                    <Row className="g-4">
                      <Col md="6">
                        <Card className="card-glass border shadow-sm h-100">
                          <CardBody>
                            <h6 className="card-title d-flex align-items-center text-primary">
                              <UploadCloud size={20} className="me-2" />
                              Carga Masiva de Alumnos
                            </h6>
                            <p className="text-muted small">
                              Sube un archivo CSV con la información de los alumnos. El sistema buscará el grado escolar y registrará los datos.
                            </p>
                            
                            <div className="border border-dashed p-3 rounded mb-3 bg-light text-center">
                              <p className="mb-1 text-muted small fw-bold">Cabeceras del CSV requeridas:</p>
                              <code>nia,names,second_names,grade,email,user,password,medicals,blood_type</code>
                            </div>

                            <div className="mb-3">
                              <Label for="studentCsvInput" className="form-label font-weight-bold">Subir archivo CSV de Alumnos</Label>
                              <Input
                                id="studentCsvInput"
                                type="file"
                                accept=".csv"
                                onChange={(e) => handleCSVUpload("students", e.target.files[0])}
                              />
                            </div>
                          </CardBody>
                        </Card>
                      </Col>

                      <Col md="6">
                        <Card className="card-glass border shadow-sm h-100">
                          <CardBody>
                            <h6 className="card-title d-flex align-items-center text-primary">
                              <UploadCloud size={20} className="me-2" />
                              Carga Masiva de Padres
                            </h6>
                            <p className="text-muted small">
                              Sube un archivo CSV con la información de los padres. El sistema vinculará a los alumnos en base al NIA indicado en la columna `childs_nia`.
                            </p>

                            <div className="border border-dashed p-3 rounded mb-3 bg-light text-center">
                              <p className="mb-1 text-muted small fw-bold">Cabeceras del CSV requeridas:</p>
                              <code>names,second_names,email,user,password,phone_1,phone_2,childs_nia</code>
                            </div>

                            <div className="mb-3">
                              <Label for="parentCsvInput" className="form-label font-weight-bold">Subir archivo CSV de Padres</Label>
                              <Input
                                id="parentCsvInput"
                                type="file"
                                accept=".csv"
                                onChange={(e) => handleCSVUpload("parents", e.target.files[0])}
                              />
                            </div>
                          </CardBody>
                        </Card>
                      </Col>

                      <Col md="12">
                        <Card className="card-glass border bg-light-primary border-primary shadow-sm mt-3">
                          <CardBody className="d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3">
                            <div>
                              <h6 className="d-flex align-items-center text-primary mb-1">
                                <Send size={20} className="me-2" /> Enviar Accesos a Todos los Padres
                              </h6>
                              <p className="text-muted mb-0 small">
                                Envía un correo con credenciales de acceso (usuario y contraseña desencriptada) a todos los padres de familia registrados en el sistema.
                              </p>
                            </div>
                            <Button color="primary" className="btn-lg text-white" onClick={handleSendEmailsAllParents}>
                              <Send size={16} className="me-2" /> Enviar Accesos Masivos
                            </Button>
                          </CardBody>
                        </Card>
                      </Col>
                    </Row>
                  </TabPane>

                  {/* TAB 4: NUCLEAR CLEAR */}
                  <TabPane tabId="4">
                    <Card className="border border-danger bg-light-danger shadow-sm">
                      <CardBody className="text-center py-5">
                        <AlertTriangle size={64} className="text-danger mb-3" />
                        <h4 className="text-danger font-weight-bold">Zona de Peligro: Limpieza Nuclear de Datos</h4>
                        <p className="text-muted mx-auto" style={{ maxWidth: "600px" }}>
                          Al presionar el siguiente botón se eliminarán de forma <b>permanente e irreversible</b> todas las tareas, ausencias registradas y listas de asistencias. Las estadísticas de monitoreo se vaciarán. Asegúrate de tener un respaldo antes de continuar.
                        </p>
                        
                        <div className="mt-4">
                          <Button color="danger" size="lg" className="px-5 py-3" onClick={handleClearAcademicData}>
                            <Trash2 size={20} className="me-2" /> Borrar Tareas, Ausencias y Listas de Asistencia
                          </Button>
                        </div>
                      </CardBody>
                    </Card>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Grade Selector Modal */}
      <Modal isOpen={gradeModal} toggle={toggleGradeModal} size="md">
        <ModalHeader toggle={toggleGradeModal}>Cambiar Salón Escolar</ModalHeader>
        <ModalBody>
          <div className="mb-4">
            <Label className="form-label font-weight-bold">Selecciona el nuevo grado o salón:</Label>
            <Typeahead
              id="bulkGradeFinder"
              clearButton
              labelKey="name"
              options={grades}
              placeholder="Elige un salón..."
              onChange={(selected) => setGradeSelected(selected)}
            />
          </div>
          <div className="d-flex justify-content-end gap-2">
            <Button color="secondary" onClick={toggleGradeModal}>Cancelar</Button>
            <Button color="primary" onClick={handleBulkChangeGrade}>Guardar cambios</Button>
          </div>
        </ModalBody>
      </Modal>

      <style jsx>{`
        .systems-dashboard {
          padding-top: 15px;
        }
        .card-glass {
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(10px);
          border-radius: 12px;
          border: 1px solid rgba(220, 224, 230, 0.8);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
        }
        .bg-light-danger {
          background-color: #fff5f5 !important;
        }
        .bg-light-primary {
          background-color: #f0f4ff !important;
        }
        .border-dashed {
          border-style: dashed !important;
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-in-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-5px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </Fragment>
  );
}

export default Systems;
